import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { WIDGET_SPACING_CLASS } from '../spacing';
import type { TextAlignment, TextContentWidgetResolved } from './types';
import { headingTag } from '@/lib/render/headingTag';
import { RichText } from '@/lib/render/RichText';
import { Text } from '@/lib/render/Text';

const ALIGN_CLASS: Record<TextAlignment, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * The Text Content Widget's kicker/title/body block, on its own so a widget can
 * carry its own heading instead of relying on a separate block above it.
 *
 * The reason is visibility, not tidiness. A Franchise-Partner who switches the
 * Lehrkräfte-Widget off used to leave its heading behind: the toggle hid the
 * carousel, and the standalone `widgets.title-widget` an editor had placed above
 * it kept rendering over nothing. Anything the toggle governs has to live inside
 * the widget the toggle addresses (SH-344 item 2.13).
 *
 * Returns `null` when there is nothing to show, so a widget can render it
 * unconditionally.
 */
export function WidgetHeading({
  resolved,
  id,
}: {
  resolved: TextContentWidgetResolved;
  /** Anchor id, when the host widget does not set one itself. */
  id?: string;
}) {
  const {
    title,
    kicker,
    body,
    alignment,
    spacing,
    anchor,
    titlePower,
    visualOnly,
  } = resolved;

  if (!title && !kicker && (!body || body.length === 0)) {
    return null;
  }

  const alignClass = ALIGN_CLASS[alignment];
  const heading = headingTag(titlePower ?? '2', visualOnly);

  return (
    <div
      id={anchor ?? id}
      className={classNames(
        // `pb-4` is an exception to the top-padding-only convention
        // (70.12-Widget-Spacing): the widget is treated as if it painted a
        // background, so the heading it provides is never flush against the
        // content it introduces.
        'scroll-mt-20 space-y-2 pb-4',
        WIDGET_SPACING_CLASS[spacing],
      )}
    >
      {kicker && (
        <Text
          as="p"
          field="kicker"
          className={classNames(
            'text-primary text-sm font-bold tracking-[0.10em] uppercase md:text-base',
            alignClass,
          )}
        >
          {kicker}
        </Text>
      )}
      {title && (
        <Text
          as={heading.as}
          field="title"
          className={classNames(heading.className, alignClass)}
        >
          {title}
        </Text>
      )}
      {body && body.length > 0 && (
        // Tailwind's preflight resets `a` to `color: inherit` with no
        // underline, and `<RichText>` styles nothing itself, so an editor's
        // link is indistinguishable from body text unless the container says
        // otherwise.
        <div
          className={classNames(
            alignClass,
            '[&_a]:text-primary [&_a]:underline',
          )}
        >
          <RichText nodes={body} />
        </div>
      )}
    </div>
  );
}
